/**
 * importOptional (#4231) - dynamic import() for OPTIONAL in-page surfaces
 * (PDF export, screenshots, the authoring demo dialog, ...).
 *
 * Why this exists: App.jsx code-splits by route, so EVERY deploy renames every
 * chunk. A tab still running the previous build asks for a chunk that no longer
 * exists. For a route page lazyWithReload (lib/lazyWithReload.js) reloads once;
 * for an optional action the learner triggered mid-session that would throw
 * away a live chat or voice session, so it must NOT reload by itself.
 *
 * This helper turns any rejection of the import into a typed OptionalChunkError.
 * The caller maps it to one honest message ("reload to get the latest version")
 * with a Reload ACTION, via reportOptionalChunkFailure below: the learner
 * chooses whether to end their session, the code never does.
 *
 * It does not memoise. A caller whose chunk carries CSS (a dialog, a panel)
 * must keep ONE import promise per document itself (see App.jsx loadHelpPanel
 * and components/LazyAuthoringDemoDialog.jsx): Vite's preload helper does not
 * forget a failed stylesheet, so a retried import() can resolve the JS without
 * its CSS. A JS-only library (html2canvas, jsPDF) is safe to retry.
 */

export class OptionalChunkError extends Error {
  constructor(key, cause) {
    super(`Optional chunk failed to load: ${key}`);
    this.name = 'OptionalChunkError';
    this.key = key;
    this.cause = cause;
  }
}

export function isOptionalChunkError(err) {
  return err instanceof OptionalChunkError;
}

/** Run `importer` (e.g. `() => import('html2canvas')`); reject with OptionalChunkError. */
export function importOptional(importer, key) {
  return Promise.resolve()
    .then(importer)
    .catch((err) => {
      throw new OptionalChunkError(key, err);
    });
}

const RELOAD_TOAST_MS = 20000;

/**
 * Show the stale-chunk toast with a Reload action. `showToast` is the
 * ToastContext function; `t` is i18next's. Never reloads on its own.
 */
export function reportOptionalChunkFailure(showToast, t, reload = () => window.location.reload()) {
  showToast(
    t('errors.chunkStale', "This part of the app couldn't load, usually because it was updated. Reload the page to get the latest version."),
    'error',
    RELOAD_TOAST_MS,
    { label: t('errors.reload', 'Reload'), onClick: reload },
  );
}
